import React, { useEffect, useMemo, useRef, useState } from "react";
import classes from "./Archive.module.css";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import DefaultDatePicker from "../../components/form/DefaultDatePicker";
import { Checkbox, Divider } from "antd";
import { ReactComponent as InfoIcon } from "../../assets/info-circle-filled.svg";
import ButtonArrangments from "../../components/form/ButtonArrangments";
import api from "../../utils/axios";
import useLang from "../../hooks/useLang";
import { toast } from "react-toastify";
import PaginationTable from "../../components/Pagination";
import DefaultInput from "../../components/form/DefaultInput";
import { ReactComponent as CodeIcon } from "../../assets/codeinput.svg";

const Archive = () => {
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [clients, setClients] = useState([]);
	const [selectedClients, setSelectedClients] = useState<number>();
	const [archiveOrders, setArchiveOrders] = useState(false);
	const [archiveSettlement, setArchiveSettlement] = useState(false);
	const { strings } = useLang("archives");
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [dateFrom, setDateFrom] = useState<Date | null>(null);
	const [dateTo, setDateTo] = useState<Date | null>(null);
	const [code, setCode] = useState<string>();

	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
			autoHeaderHeight: true,
		}),
		[]
	);

	let grid = useRef<any>(null);

	const nameAndSurnameGetter = (params: any) => {
		const text = params.data.firstName + " " + params.data.lastName;
		return text;
	};

	const getClientsData = async () => {
		try {
			const response = await api.get("User/clients");
			if (response.data) {
				setClients(response?.data);
			}
		} catch (error: any) {
			console.log(error);
		}
	};

	const filteredData = useMemo(() => {
		return clients.filter((item: any) => {
			return (
				!code ||
				item.id.toString().includes(code) ||
				(item.firstName &&
					item.firstName
						?.toLowerCase()
						.includes(code?.toLowerCase())) ||
				(item.lastName &&
					item.lastName
						?.toLowerCase()
						.includes(code?.toLowerCase())) ||
				(item.username &&
					item.username
						?.toLowerCase()
						.includes(code?.toLowerCase())) ||
				(item.businessName &&
					item.businessName
						?.toLowerCase()
						.includes(code?.toLowerCase()))
			);
		});
	}, [clients, code]);

	const paginatedClients = useMemo(() => {
		const dataToPaginate = filteredData;
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return dataToPaginate.slice(startIndex, endIndex);
	}, [currentPage, pageSize, filteredData]);

	const totalClients = filteredData.length;
	const startOrderIndex = (currentPage - 1) * pageSize + 1;
	const endOrderIndex = Math.min(
		startOrderIndex + pageSize - 1,
		totalClients
	);

	const onPageChanged = (page: number) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (current: number, size: number) => {
		setPageSize(size);
		setCurrentPage(1);
	};

	useEffect(() => {
		getClientsData();
		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 60,
			},
			{
				field: "id",
				headerName: "Id",
				width: 150,
			},
			{
				field: "firstName",
				valueGetter: nameAndSurnameGetter,
				headerName: strings?.headerNameFirstLastName,
				width: 150,
			},
			{
				field: "orderCount",
				headerName: strings?.headerNameOrderCount,
				width: 120,
			},
			{
				field: "settlementCount",
				headerName: strings?.headerNameSettlementCount,
				width: 150,
			},
			{
				field: "archivedOrders",
				headerName: strings?.headerNameArchivedOrders,
				width: 170,
			},
			{
				field: "archivedSettlement",
				headerName: strings?.headerNameArchivedSettlement,
				width: 180,
			},
		]);
	}, [setTableColumns, strings]);

	const handleArchiveChange = (e: any) => {
		const selectedRows = grid.current.api.getSelectedRows();
		if (selectedRows.length > 0) {
			const selectedClientIds = selectedRows.map((row: any) => row.id);
			setSelectedClients(selectedClientIds);
		} else {
			console.log(strings?.noClientsSelected);
		}
	};

	const handleArchiveOrdersChange = (e: any) => {
		setArchiveOrders(e.target.checked);
	};

	const handleArchiveSettlementChange = (e: any) => {
		setArchiveSettlement(e.target.checked);
	};

	const clearStates = () => {
		setDateFrom(null);
		setDateTo(null);
		setArchiveOrders(false);
		setArchiveSettlement(false);
	};

	const handleArchiveButtonClick = async () => {
		if (!archiveOrders && !archiveSettlement) {
			toast.error("Duhet te zgjedhni llojin e arkivimit");
			return;
		}
		if (!dateFrom || !dateTo) {
			toast.error(
				"Të dyja fushat DateFrom dhe DateTo duhet të plotësohen."
			);
			return;
		}
		const requestData = {
			clientIds: selectedClients,
			dateFrom: dateFrom,
			dateTo: dateTo,
		};
		if (archiveOrders) {
			try {
				const response = await api.post(
					"Order/archive-orders",
					requestData
				);
				if (response.status === 200) {
					toast.success(strings?.orderArchiveSuccess);
					getClientsData();
					clearStates();
				}
			} catch (error) {
				console.log(strings?.failedToArchiveOrders, error);
			}
		}
		if (archiveSettlement) {
			try {
				const response = await api.post(
					"Settlement/archive-settlement",
					requestData
				);
				if (response.status === 200) {
					toast.success(strings?.settlementArchiveSuccess);
					getClientsData();
					clearStates();
				}
			} catch (error) {
				console.log(strings?.failedToArchiveSettlement, error);
			}
		}
	};

	const handleDatePrejChange = (date: Date | any) => {
		setDateFrom(date);

		if (dateFrom && new Date(dateFrom) > new Date(date)) {
			toast.error(
				"Gabim: DateFrom nuk mund të jetë më e madhe se DateTo."
			);
			setDateTo(null);
			setDateFrom(null);
		}
	};

	const handleDateDeriChange = (date: Date | any) => {
		setDateTo(date);

		if (dateFrom && new Date(dateFrom) > new Date(date)) {
			toast.error(
				"Gabim: DateFrom nuk mund të jetë më e madhe se DateTo."
			);
			setDateTo(null);
			setDateFrom(null);
		}
	};

	return (
		<div className={classes.mainContainer}>
			<h1 className={classes.title}>{strings?.title}</h1>
			<div className={classes.heroContainer}>
				<div className={classes.firstContainer}>
					<p className={classes.firstContainerTitle}>
						{strings?.firstContainerTitle}
					</p>
					<PaginationTable
						current={currentPage}
						pageSize={pageSize}
						total={totalClients}
						startOrderIndex={startOrderIndex}
						endOrderIndex={endOrderIndex}
						onChange={onPageChanged}
						onShowSizeChange={handlePageSizeChange}
					/>
					<div style={{ width: 250, height: "3.65rem" }}>
						<DefaultInput
							label={"Kerko"}
							// className={classes.input}
							prefix={<CodeIcon />}
							placeholder={"Kerko"}
							value={code}
							onChange={(e) => {
								setCurrentPage(1);
								setCode(e.target.value);
							}}
						/>
					</div>
					<div
						className="ag-theme-alpine"
						style={{ width: "100%", height: "500" }}
					>
						<AgGridReact
							rowData={paginatedClients}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							rowSelection="multiple"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							domLayout="autoHeight"
							suppressPaginationPanel={false}
							rowHeight={60}
							onSelectionChanged={handleArchiveChange}
						/>
					</div>
				</div>
				<div className={classes.secondContainer}>
					<p className={classes.firstContainerTitle}>
						{strings?.firstContainerTitle}
					</p>
					<div className={classes.dateDiv}>
						<DefaultDatePicker
							label={strings?.datePrej}
							value={dateFrom}
							onChange={handleDatePrejChange}
						/>
						<DefaultDatePicker
							label={strings?.dateDeri}
							value={dateTo}
							onChange={handleDateDeriChange}
						/>
					</div>
					<div className={classes.typeDiv}>
						<p className={classes.firstContainerTitle}>
							{strings?.typeDivTitle}
						</p>
						<p>
							<Checkbox
								checked={archiveOrders}
								onChange={handleArchiveOrdersChange}
							/>{" "}
							{strings?.archiveOrders}
						</p>
						<p>
							<Checkbox
								checked={archiveSettlement}
								onChange={handleArchiveSettlementChange}
							/>{" "}
							{strings?.archiveSettlement}
						</p>
					</div>
					<div className={classes.note}>
						<InfoIcon width={80} />
						<div>
							<p className={classes.noteTitle}>
								{strings?.noteTitle}
							</p>
							<p>{strings?.noteText1}</p>
						</div>
					</div>
					<div className={classes.note}>
						<InfoIcon width={70} />
						<div className="gap-2">
							<p className={classes.noteTitle}>
								{strings?.noteTitle}
							</p>
							<p>{strings?.noteText2}</p>
						</div>
					</div>
				</div>
			</div>
			<Divider />
			<ButtonArrangments
				text={strings?.buttonText}
				onClick={handleArchiveButtonClick}
			/>
		</div>
	);
};

export default Archive;
